import type { Character } from '../domain/character.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import { inEncounter } from '../domain/runtime.ts';
import type { InventoryCatalogue } from '../rules/inventory.ts';
import type { HotlistUseChoice } from '../rules/hotlistUse.ts';
import { hotlistActionAffordability, planHotlistUse } from '../rules/hotlistUse.ts';
import { spendEncounterAction } from './encounterService.ts';

/**
 * RULE DCC-HOT-007 - the **Use a Hotlist Item** Action, withdrawal arm.
 *
 * Rules p.48: "Crawlers can spend an Action to activate something from their
 * Hotlist." C&C p.15: "When you have a moment outside of combat, or an Action
 * in combat, any item in your Hotlist can instantly appear in a free hand."
 * So: one Action in a fight, free outside one.
 *
 * PURE-THEN-PERSIST, the E1 shape. This works out every record the Action would
 * change and returns them; the caller commits them together through the
 * existing boundary. It writes nothing, so a refusal cannot leave anything
 * half-applied.
 *
 * **THIS SERVICE DOES NOT CONSUME ANYTHING, AND MUST NOT.**
 *
 * The Action has two printed arms (Rules p.19, Table 2): use a prepared
 * consumable, or ready another Hotlist Item. The first arm was BUILT AND
 * VERIFIED IN E4 - `itemUseService.useItem` already applies the authored
 * effect, consumes the copy, spends exactly one Action through
 * `spendEncounterAction`, runs the potion cadence and inflicts Potion Sickness.
 * E4's potion pathway IS that arm.
 *
 * So this file builds ONLY the second arm, and spends ONLY its own Action.
 * Wrapping E4's use in a second Action-spending service would charge a crawler
 * two Actions for one drink - the same double-spend defect E1 had to resolve
 * against the manual Spend an Action button. There is a regression for it.
 *
 * ORDINARY CONFIGURATION IS STILL LOCKED IN COMBAT. `hotlistService` and
 * `inventoryService` refuse during an encounter (RULE DCC-HOT-021,
 * DCC-INV-013) and neither has a bypass flag. This is the priced pathway the
 * refusal points at: a DIFFERENT function that takes the real runtime, spends
 * the Action, and commits both records together. It deliberately does not call
 * `equipOne` or `unequipOne`, which would be that bypass.
 *
 * NO CHANGELOG ENTRY, and that is a decision rather than an omission. A paid
 * withdrawal is a gameplay mutation coupled to a spent Action in the Runtime.
 * Making the Character half undoable without also unwinding the Action would
 * offer a misleading partial Undo, so this follows E3 and E4: the pure planner
 * builds the next Character, the service builds the next Runtime, and
 * `persistMutation` writes them atomically. `CharacterChange` remains the
 * pathway for MANUAL configuration, which is a different thing.
 */

export type HotlistUseServiceOutcome =
  | {
      ok: true;
      character: Character;
      /** UNCHANGED BY REFERENCE outside a fight, so no journal is opened. */
      runtime: RuntimeState;
      /** For the sheet to report what happened, in order. */
      lines: string[];
      /** True when this Action came out of the encounter's pool. */
      actionSpent: boolean;
    }
  | { ok: false; reason: string };

export interface HotlistUseDeps {
  catalogue: InventoryCatalogue;
}

export function useHotlistItem(
  character: Character,
  runtime: RuntimeState,
  choice: HotlistUseChoice,
  deps: HotlistUseDeps,
): HotlistUseServiceOutcome {
  // 1. IDENTITY. A RuntimeState is only evidence about the crawler it names. A
  // stale one from a screen that switched crawlers must not answer "are you in
  // a fight" about somebody else - a quiet runtime for crawler B is not
  // evidence that crawler A is out of combat. Same wording and same position as
  // `inventoryService`, `hotlistService` and `itemUseService`.
  if (runtime.characterId !== character.id) {
    return {
      ok: false,
      reason:
        `Internal error: this Hotlist Action is for ${character.id} but the runtime supplied ` +
        `belongs to ${runtime.characterId}. Nothing has been changed. Reopen the crawler and ` +
        'try again.',
    };
  }

  const fighting = inEncounter(runtime);

  // 2. AFFORDABILITY, as a preflight, from the SHARED helper the panel also
  // asks. A refusal must cost no Action and change no Item, so this is asked
  // before anything is built - and it is re-asked here rather than trusted
  // from the interface, because a rendered control describes a moment that has
  // already passed.
  const unaffordable = hotlistActionAffordability(runtime, character.id, fighting);
  if (unaffordable !== null) return { ok: false, reason: unaffordable };

  // 3. LEGALITY, from the shared pure planner. The panel asks the same
  // function, so a control is never offered for something this would refuse.
  const planned = planHotlistUse(character, choice, deps.catalogue);
  if (!planned.ok) return { ok: false, reason: planned.reason };

  const incomingName =
    deps.catalogue.lookupItem(planned.plan.incomingItemId)?.name ?? planned.plan.incomingItemId;
  const lines: string[] = [];
  if (planned.plan.outgoingItemId === null) {
    lines.push(`${incomingName} is ready in hand.`);
  } else {
    const outgoingName =
      deps.catalogue.lookupItem(planned.plan.outgoingItemId)?.name ?? planned.plan.outgoingItemId;
    lines.push(
      `${outgoingName} went back to Hotlist slot ${(planned.plan.outgoingSlot ?? 0) + 1}; ` +
        `${incomingName} is ready in hand.`,
    );
  }

  // 4. THE ACTION, through the shared pathway so `after-next-action` expiry
  // fires exactly as it does for every other Action. Never by decrementing
  // `actionsRemaining` here. Outside a fight the runtime is returned BY
  // REFERENCE, which is what lets `persistMutation` take the single-record path
  // rather than opening a journal for a record nobody touched.
  let nextRuntime = runtime;
  let actionSpent = false;
  if (fighting) {
    const spent = spendEncounterAction(nextRuntime, character.id, 1, 'normal');
    if (!spent.ok) return { ok: false, reason: spent.reason };
    nextRuntime = spent.runtime;
    actionSpent = true;
    for (const id of spent.expiredEffectIds) lines.push(`${id} ended with that Action`);
  }

  return { ok: true, character: planned.character, runtime: nextRuntime, lines, actionSpent };
}
